import React, { useState, useEffect } from 'react';
import { FileSpreadsheet, Search, Download, Calendar, X, Trophy, TrendingUp, Clock, AlertTriangle, ChevronDown, ChevronRight, Users, FileText, Bot, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Componente de Árbol Desplegable
function UnionClubTree({ jugadores, buyInFichas, aplicarRatio, nombreMesa }) {
  const [expandedUniones, setExpandedUniones] = useState({});
  const [expandedClubs, setExpandedClubs] = useState({});
  
  // Detectar si es evento KO
  const esEventoKO = nombreMesa && nombreMesa.toUpperCase().includes('KO');
  
  // METAS FIJAS por Unión (según documento de overlay)
  const metasPorUnion = {
    '1313': 2.0,
    '1534': 12.0,
    '1578': 12.0,
    '1675': 60.0,
    '1765': 0.0,
    '2006': 8.0,
    '2101': 0.0,
    '2126': 4.0,
    '2343': 2.0,
    '2448': 0.0,
    '2652': 0.0,
    '3086': 0.0,
    '2474': 0.0
  };
  
  // Calcular total de entradas del torneo
  // Suma todas las entrada_fichas y divide por buy_in TOTAL (fichas + rake)
  const sumaEntradaFichas = jugadores.reduce((sum, j) => sum + (j.entrada_fichas || 0), 0);
  const totalEntradas = buyInFichas > 0 ? Math.floor(sumaEntradaFichas / buyInFichas) : jugadores.length;
  
  // Agrupar jugadores por Unión → Club
  const estructura = {};
  jugadores.forEach(j => {
    const unionId = j.union_id || 'Sin Unión';
    const clubId = j.club_id || 'Sin Club';
    
    // Formato: "Club [ID] - [Nombre]" o solo "Club [ID]" si no hay nombre
    let nombreClub;
    if (j.nombre_club && j.nombre_club.trim() !== '') {
      nombreClub = `Club ${clubId} - ${j.nombre_club}`;
    } else {
      nombreClub = `Club ${clubId}`;
    }
    
    if (!estructura[unionId]) {
      estructura[unionId] = { 
        clubs: {},
        sumaEntradaFichas: 0
      };
    }
    if (!estructura[unionId].clubs[clubId]) {
      estructura[unionId].clubs[clubId] = {
        nombre: nombreClub,
        jugadores: [],
        sumaEntradaFichas: 0
      };
    }
    estructura[unionId].clubs[clubId].jugadores.push(j);
    
    // Sumar entrada_fichas para la unión y el club
    const entradaFichas = j.entrada_fichas || 0;
    estructura[unionId].sumaEntradaFichas += entradaFichas;
    estructura[unionId].clubs[clubId].sumaEntradaFichas += entradaFichas;
  });
  
  const toggleUnion = (unionId) => {
    setExpandedUniones(prev => ({
      ...prev,
      [unionId]: !prev[unionId]
    }));
  };
  
  const toggleClub = (unionId, clubId) => {
    const key = `${unionId}-${clubId}`;
    setExpandedClubs(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  
  return (
    <div className="space-y-2">
      {Object.entries(estructura)
        .sort(([, a], [, b]) => {
          // Ordenar por total de entradas (suma_entrada_fichas / buy_in_fichas)
          const entradasA = buyInFichas > 0 ? Math.floor(a.sumaEntradaFichas / buyInFichas) : 0;
          const entradasB = buyInFichas > 0 ? Math.floor(b.sumaEntradaFichas / buyInFichas) : 0;
          return entradasB - entradasA;
        })
        .map(([unionId, unionData]) => {
          // Calcular total de entradas de la unión: suma entrada_fichas / buy_in_fichas
          const totalEntradasUnion = buyInFichas > 0 
            ? Math.floor(unionData.sumaEntradaFichas / buyInFichas)
            : 0;
          
          // Total de jugadores únicos
          const totalJugadoresUnion = unionData.clubs 
            ? Object.values(unionData.clubs).reduce((sum, club) => sum + club.jugadores.length, 0) 
            : 0;
            
          const totalClubs = Object.keys(unionData.clubs).length;
          
          // Calcular % de participación real (basado en entradas)
          const pctReal = totalEntradas > 0 
            ? (totalEntradasUnion / totalEntradas * 100) 
            : 0;
          
          // Obtener meta fija de la unión
          const pctMeta = metasPorUnion[unionId] || 0;
          
          // Calcular diferencia: Real - Meta
          // Positivo = superó la meta, Negativo = no alcanzó la meta
          const diferencia = pctReal - pctMeta;
          
          return (
            <div key={unionId} className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
              {/* UNIÓN - Nivel 1 */}
              <button
                onClick={() => toggleUnion(unionId)}
                className="w-full bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30 hover:from-purple-200 hover:to-blue-200 dark:hover:from-purple-800/40 dark:hover:to-blue-800/40 p-4 flex items-center justify-between transition-colors"
              >
                <div className="flex items-center gap-3">
                  {expandedUniones[unionId] ? (
                    <ChevronDown className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  )}
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  <span className="font-bold text-lg text-gray-900 dark:text-white">
                    Unión {unionId}
                  </span>
                </div>
                <div className="flex gap-2 text-sm items-center flex-wrap justify-end">
                  <span className="bg-purple-200 dark:bg-purple-800 text-purple-900 dark:text-purple-200 px-3 py-1 rounded-full font-semibold">
                    {totalClubs} clubs
                  </span>
                  <span className="bg-blue-200 dark:bg-blue-800 text-blue-900 dark:text-blue-200 px-3 py-1 rounded-full font-semibold">
                    {totalEntradasUnion} entradas
                  </span>
                  <span className="bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-200 px-3 py-1 rounded-full font-semibold">
                    {totalJugadoresUnion} jugadores
                  </span>
                  <span className="bg-green-200 dark:bg-green-800 text-green-900 dark:text-green-200 px-3 py-1 rounded-full font-semibold">
                    {pctReal.toFixed(1)}% real
                  </span>
                  <span className={`px-3 py-1 rounded-full font-bold ${
                    diferencia >= 0
                      ? 'bg-green-200 dark:bg-green-800 text-green-900 dark:text-green-200' 
                      : 'bg-red-200 dark:bg-red-800 text-red-900 dark:text-red-200'
                  }`}>
                    {diferencia >= 0 ? '+' : ''}{diferencia.toFixed(1)}%
                  </span>
                </div>
              </button>
              
              {/* CLUBS - Nivel 2 */}
              {expandedUniones[unionId] && (
                <div className="bg-white dark:bg-gray-800">
                  {Object.entries(unionData.clubs)
                    .sort(([, a], [, b]) => {
                      // Ordenar por total de entradas del club (suma_entrada_fichas / buy_in_fichas)
                      const entradasA = buyInFichas > 0 ? Math.floor(a.sumaEntradaFichas / buyInFichas) : 0;
                      const entradasB = buyInFichas > 0 ? Math.floor(b.sumaEntradaFichas / buyInFichas) : 0;
                      return entradasB - entradasA;
                    })
                    .map(([clubId, clubData]) => {
                      const clubKey = `${unionId}-${clubId}`;
                      
                      // Calcular entradas y jugadores del club (dividir por buy_in_fichas)
                      const entradasClub = buyInFichas > 0 
                        ? Math.floor(clubData.sumaEntradaFichas / buyInFichas)
                        : 0;
                      const jugadoresClub = clubData.jugadores.length;
                      
                      return (
                        <div key={clubId} className="border-t border-gray-200 dark:border-gray-700">
                          <button
                            onClick={() => toggleClub(unionId, clubId)}
                            className="w-full bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 p-3 pl-12 flex items-center justify-between transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              {expandedClubs[clubKey] ? (
                                <ChevronDown className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                              )}
                              <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                              <span className="font-semibold text-gray-900 dark:text-white">
                                {clubData.nombre}
                              </span>
                            </div>
                            <div className="flex gap-2">
                              <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 px-3 py-1 rounded-full text-sm font-semibold">
                                {entradasClub} entradas
                              </span>
                              <span className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 px-3 py-1 rounded-full text-sm font-semibold">
                                {jugadoresClub} jugadores
                              </span>
                            </div>
                          </button>
                          
                          {/* JUGADORES - Nivel 3 */}
                          {expandedClubs[clubKey] && (
                            <div className="bg-white dark:bg-gray-800 p-4 pl-16">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-100 dark:bg-gray-700">
                                  <tr>
                                    <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300">ID</th>
                                    <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300">Apodo</th>
                                    <th className="px-3 py-2 text-center font-semibold text-gray-700 dark:text-gray-300">Entrada</th>
                                    <th className="px-3 py-2 text-center font-semibold text-gray-700 dark:text-gray-300">Ranking</th>
                                    <th className="px-3 py-2 text-right font-semibold text-gray-700 dark:text-gray-300">Ganancia</th>
                                    {esEventoKO && (
                                      <th className="px-3 py-2 text-right font-semibold text-gray-700 dark:text-gray-300">Recompensa</th>
                                    )}
                                    <th className="px-3 py-2 text-right font-semibold text-gray-700 dark:text-gray-300">Cuota</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                                  {clubData.jugadores
                                    .sort((a, b) => (a.ranking || 999) - (b.ranking || 999))
                                    .map((jugador, idx) => {
                                      // Mostrar entrada_fichas tal cual viene del Excel (dato crudo)
                                      const entradaFichas = jugador.entrada_fichas || 0;
                                      
                                      return (
                                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                          <td className="px-3 py-2 font-mono text-xs text-gray-600 dark:text-gray-400">
                                            {jugador.jugador_id}
                                          </td>
                                          <td className="px-3 py-2 font-medium text-gray-900 dark:text-white">
                                            {jugador.apodo || '-'}
                                          </td>
                                          <td className="px-3 py-2 text-center">
                                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                              entradaFichas > buyInFichas
                                                ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
                                                : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                            }`}>
                                              {entradaFichas}
                                            </span>
                                          </td>
                                          <td className="px-3 py-2 text-center text-gray-700 dark:text-gray-300">
                                            {jugador.ranking || '-'}
                                          </td>
                                          <td className={`px-3 py-2 text-right font-semibold ${
                                            (jugador.ganancia || 0) > 0
                                              ? 'text-green-600 dark:text-green-400'
                                              : 'text-gray-500 dark:text-gray-400'
                                          }`}>
                                            {aplicarRatio(jugador.ganancia || 0).toFixed(2)}
                                          </td>
                                          {esEventoKO && (
                                            <td className={`px-3 py-2 text-right font-semibold ${
                                              (jugador.recompensa || 0) > 0
                                                ? 'text-green-600 dark:text-green-400'
                                                : 'text-gray-500 dark:text-gray-400'
                                            }`}>
                                              {aplicarRatio(jugador.recompensa || 0).toFixed(2)}
                                            </td>
                                          )}
                                          <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                                            {aplicarRatio(jugador.cuota || 0).toFixed(2)}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
}

export default function PPSTInforme() {
  const [loading, setLoading] = useState(false);
  
  // Ratio de conversión
  const [ratio, setRatio] = useState('1:6'); // '1:6', '1:1', '1:5'
  
  // Filtros (PPST fijo)
  const tipo = 'PPST';
  const [snapshots, setSnapshots] = useState([]);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [allUniones, setAllUniones] = useState([]);
  const [selectedUnionId, setSelectedUnionId] = useState('');
  const [allClubs, setAllClubs] = useState([]);
  const [selectedClubId, setSelectedClubId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filtros específicos PPST
  const [selectedModalidad, setSelectedModalidad] = useState('');
  const [modalidades, setModalidades] = useState([]);
  const [soloConOverlay, setSoloConOverlay] = useState(false);
  const [incluirSpins, setIncluirSpins] = useState(false); // Por defecto NO incluir spins
  const [buyInMin, setBuyInMin] = useState('');
  const [buyInMax, setBuyInMax] = useState('');
  
  // Datos mostrados
  const [torneos, setTorneos] = useState([]);
  const [hasFilters, setHasFilters] = useState(false);
  
  // Ordenamiento
  const [ordenarPor, setOrdenarPor] = useState('fecha'); // fecha, buyin, overlay, jugadores, gtd, nombre
  
  // Modal de torneo
  const [showTorneoModal, setShowTorneoModal] = useState(false);
  const [torneoSeleccionado, setTorneoSeleccionado] = useState(null);
  const [jugadoresTorneo, setJugadoresTorneo] = useState([]);
  const [loadingTorneo, setLoadingTorneo] = useState(false);
  
  // Modal de DESGLOSE (nuevo)
  const [showDesgloseModal, setShowDesgloseModal] = useState(false);
  const [torneoDesglose, setTorneoDesglose] = useState(null);
  const [desglosePorUnion, setDesglosePorUnion] = useState([]);
  const [jugadoresDesglose, setJugadoresDesglose] = useState([]);
  const [loadingDesglose, setLoadingDesglose] = useState(false);
  
  // Filtros del modal
  const [filtroClubTorneo, setFiltroClubTorneo] = useState('');
  const [filtroJugadorTorneo, setFiltroJugadorTorneo] = useState('');
  
  // Búsqueda por ID de jugador
  const [busquedaJugadorId, setBusquedaJugadorId] = useState('');
  const [tipoBusqueda, setTipoBusqueda] = useState('jugador');
  const [busquedaClubId, setBusquedaClubId] = useState('');
  const [busquedaUnionId, setBusquedaUnionId] = useState('');
  const [resultadoClub, setResultadoClub] = useState(null);
  const [resultadoUnion, setResultadoUnion] = useState(null);
  const [loadingBusqueda, setLoadingBusqueda] = useState(false);
  const [detallesJugador, setDetallesJugador] = useState([]);
  const [modoVistaJugador, setModoVistaJugador] = useState(false);
  
  // Estadísticas
  const [estadisticas, setEstadisticas] = useState(null);
  const [mostrarEstadisticas, setMostrarEstadisticas] = useState(false);
  
  // Análisis Comparativo
  const [mostrarComparativo, setMostrarComparativo] = useState(false);
  const [periodo1Desde, setPeriodo1Desde] = useState('');
  const [periodo1Hasta, setPeriodo1Hasta] = useState('');
  const [periodo2Desde, setPeriodo2Desde] = useState('');
  const [periodo2Hasta, setPeriodo2Hasta] = useState('');
  const [datosComparativos, setDatosComparativos] = useState(null);
  const [loadingComparativo, setLoadingComparativo] = useState(false);

  // Rol del usuario
  const userStored = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = userStored.role !== 'worker';

  // Control Bot PPST
  const [loadingControlBotPPST, setLoadingControlBotPPST] = useState(false);

  
  // Análisis Resultados Bot PPST
  const [loadingProcesarPPST, setLoadingProcesarPPST] = useState(false);
  const [archivoResultadosPPST, setArchivoResultadosPPST] = useState(null);
  const [semanaInicioBotPPST, setSemanaInicioBotPPST] = useState('');
  const [semanaFinBotPPST, setSemanaFinBotPPST] = useState('');
  const [incluyePerdedoresPPST, setIncluyePerdedoresPPST] = useState(false);
  const [historialInformesPPST, setHistorialInformesPPST] = useState([]);



  // Función para aplicar ratio de conversión
  const aplicarRatio = (valor) => {
    if (!valor) return 0;
    switch (ratio) {
      case '1:1':
        return valor / 6;
      case '1:5':
        return (valor / 6) * 5;
      case '1:6':
      default:
        return valor;
    }
  };

  useEffect(() => {
    loadSnapshots();
  }, []);
  
  // Cargar modalidades al montar el componente
  useEffect(() => {
    const fetchModalidades = async () => {
      try {
        const token = localStorage.getItem('token');
        const resp = await axios.get(`${API}/superunion/ppst/modalidades`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (resp.data.success) setModalidades(resp.data.modalidades);
      } catch (err) {
        setModalidades(['NLH', 'PLO5', 'SPINUP']);
      }
    };
    fetchModalidades();
  }, []);

  // Cargar uniones cuando cambia el rango de fechas
  useEffect(() => {
    if (fechaDesde || fechaHasta) {
      cargarUniones();
    }
  }, [fechaDesde, fechaHasta]);
  
  // Cargar clubs cuando cambia la unión seleccionada
  useEffect(() => {
    if (selectedUnionId) {
      cargarClubs();
    } else {
      setAllClubs([]);
      setSelectedClubId('');
    }
  }, [selectedUnionId, fechaDesde, fechaHasta]);
  
  // Re-ordenar torneos cuando cambia el criterio de ordenamiento
  useEffect(() => {
    if (torneos.length > 0) {
      const torneosOrdenados = [...torneos];
      torneosOrdenados.sort((a, b) => {
        switch (ordenarPor) {
          case 'fecha':
            return new Date(b.fecha || 0) - new Date(a.fecha || 0);
          case 'buyin':
            return (b.buy_in || 0) - (a.buy_in || 0);
          case 'overlay':
            return (a.overlay || 0) - (b.overlay || 0);
          case 'jugadores':
            return (b.total_jugadores || 0) - (a.total_jugadores || 0);
          case 'gtd':
            return (b.gtd || 0) - (a.gtd || 0);
          case 'nombre':
            const nombreA = (a.nombre_mesa || '').toLowerCase();
            const nombreB = (b.nombre_mesa || '').toLowerCase();
            return nombreA.localeCompare(nombreB);
          default:
            return 0;
        }
      });
      setTorneos(torneosOrdenados);
    }
  }, [ordenarPor]); // Solo se ejecuta cuando cambia ordenarPor

  const cargarUniones = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);
      
      const response = await axios.get(
        `${API}/superunion/ppst/uniones?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setAllUniones(response.data.uniones || []);
      }
    } catch (error) {
      console.error('Error cargando uniones:', error);
    }
  };
  
  const cargarClubs = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);
      if (selectedUnionId) params.append('union_id', selectedUnionId);
      
      const response = await axios.get(
        `${API}/superunion/ppst/clubs?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setAllClubs(response.data.clubs || []);
      }
    } catch (error) {
      console.error('Error cargando clubs:', error);
    }
  };

  const loadSnapshots = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/superunion/snapshots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const filtered = response.data.snapshots.filter(s => s.tipo === tipo);
      setSnapshots(filtered);
      
      if (filtered.length > 0) {
        // Establecer rango de fechas inicial (última semana)
        const fechas = filtered.map(s => s.fecha).sort();
        if (fechas.length > 0) {
          setFechaHasta(fechas[fechas.length - 1]);
          if (fechas.length >= 7) {
            setFechaDesde(fechas[fechas.length - 7]);
          } else {
            setFechaDesde(fechas[0]);
          }
        }
        
        // Cargar datos iniciales
        loadInitialData(filtered);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar datos');
    }
  };

  const loadInitialData = async (snapshotsData) => {
    try {
      const token = localStorage.getItem('token');
      
      console.log('🏆 Cargando datos PPST iniciales...', snapshotsData.length, 'snapshots');
      
      if (snapshotsData.length === 0) {
        console.warn('⚠️ No hay snapshots PPST disponibles');
        toast.info('No hay datos de torneos (PPST) cargados aún');
        return;
      }
      
      // Extraer uniones únicas
      const unionesSet = new Set();
      const clubsSet = new Set();
      
      snapshotsData.forEach(snapshot => {
        if (snapshot.data && Array.isArray(snapshot.data)) {
          snapshot.data.forEach(torneo => {
            if (torneo.union_id) unionesSet.add(torneo.union_id);
            if (torneo.club_id) clubsSet.add(torneo.club_id);
          });
        }
      });
      
      setAllUniones(Array.from(unionesSet).sort());
      setAllClubs(Array.from(clubsSet).sort());
      
      console.log('✅ Datos iniciales cargados:', unionesSet.size, 'uniones,', clubsSet.size, 'clubs');
      
    } catch (error) {
      console.error('Error cargando datos iniciales:', error);
      toast.error('Error al cargar datos iniciales');
    }
  };

  const aplicarFiltros = async () => {
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Construir query params
      const params = new URLSearchParams();
      
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);
      if (selectedUnionId) params.append('union_id', selectedUnionId);
      if (selectedClubId) params.append('club_id', selectedClubId);
      if (selectedModalidad) params.append('modalidad', selectedModalidad);
      if (soloConOverlay) params.append('solo_overlay', 'true');
      // No enviar límite, el backend lo calculará dinámicamente
      
      // Consultar torneos directamente desde el backend
      const response = await axios.get(
        `${API}/superunion/ppst/torneos?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (!response.data.success) {
        throw new Error('Error al obtener torneos');
      }
      
      let torneosData = response.data.torneos || [];
      
      // EXCLUIR SPIN & GO por defecto (a menos que se active el checkbox)
      if (!incluirSpins && selectedModalidad !== 'SPINUP') {
        torneosData = torneosData.filter(t => t.modalidad !== 'SPINUP');
      }
      
      // Filtros adicionales en frontend
      if (buyInMin) {
        torneosData = torneosData.filter(t => t.buy_in >= parseFloat(buyInMin));
      }
      if (buyInMax) {
        torneosData = torneosData.filter(t => t.buy_in <= parseFloat(buyInMax));
      }
      
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        torneosData = torneosData.filter(t => 
          (t.partida_id && t.partida_id.toLowerCase().includes(query)) ||
          (t.nombre_mesa && t.nombre_mesa.toLowerCase().includes(query))
        );
      }
      
      // ORDENAMIENTO según selección
      torneosData.sort((a, b) => {
        switch (ordenarPor) {
          case 'fecha':
            // Más reciente primero
            return new Date(b.fecha || 0) - new Date(a.fecha || 0);
          
          case 'buyin':
            // Mayor buy-in primero
            return (b.buy_in || 0) - (a.buy_in || 0);
          
          case 'overlay':
            // Mayor overlay primero (más negativo = más importante)
            return (a.overlay || 0) - (b.overlay || 0);
          
          case 'jugadores':
            // Más jugadores primero
            return (b.total_jugadores || 0) - (a.total_jugadores || 0);
          
          case 'gtd':
            // Mayor GTD primero
            return (b.gtd || 0) - (a.gtd || 0);
          
          case 'nombre':
            // Orden alfabético (A-Z)
            const nombreA = (a.nombre_mesa || '').toLowerCase();
            const nombreB = (b.nombre_mesa || '').toLowerCase();
            return nombreA.localeCompare(nombreB);
          
          default:
            return 0;
        }
      });
      
      setTorneos(torneosData);
      setHasFilters(true);
      
      toast.success(`${torneosData.length} torneos encontrados`);
      
    } catch (error) {
      console.error('Error aplicando filtros:', error);
      toast.error('Error al aplicar filtros: ' + (error.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  const limpiarFiltros = () => {
    setFechaDesde('');
    setFechaHasta('');
    setSelectedUnionId('');
    setSelectedClubId('');
    setSelectedModalidad('');
    setSoloConOverlay(false);
    setIncluirSpins(false);
    setBuyInMin('');
    setBuyInMax('');
    setSearchQuery('');
    setBusquedaJugadorId('');
    setBusquedaClubId('');
    setBusquedaUnionId('');
    setTorneos([]);
    setHasFilters(false);
    setModoVistaJugador(false);
    setDetallesJugador([]);
    setResultadoClub(null);
    setResultadoUnion(null);
  };

  const buscarPorClubId = async () => {
    if (!busquedaClubId.trim()) { toast.error('Ingrese un ID de club'); return; }
    setLoadingBusqueda(true);
    setResultadoClub(null);
    setResultadoUnion(null);
    try {
      const token = localStorage.getItem('token');
      const params = { club_id: busquedaClubId.trim() };
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      const res = await axios.get(`${API}/superunion/ppst/buscar-club`, {
        headers: { Authorization: `Bearer ${token}` }, params
      });
      if (res.data.success) {
        setResultadoClub(res.data);
        setTorneos(res.data.torneos || []);
        setHasFilters(true);
        setModoVistaJugador(false);
        toast.success(`Club ${res.data.nombre_club}: ${res.data.jugadores_activos} jugadores activos, ${res.data.torneos_participados} torneos`);
      }
    } catch (e) { toast.error('Error al buscar club: ' + (e.response?.data?.detail || e.message)); }
    finally { setLoadingBusqueda(false); }
  };

  const buscarPorUnionId = async () => {
    if (!busquedaUnionId.trim()) { toast.error('Ingrese un ID de union'); return; }
    setLoadingBusqueda(true);
    setResultadoClub(null);
    setResultadoUnion(null);
    try {
      const token = localStorage.getItem('token');
      const params = { union_id: busquedaUnionId.trim() };
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      const res = await axios.get(`${API}/superunion/ppst/buscar-union`, {
        headers: { Authorization: `Bearer ${token}` }, params
      });
      if (res.data.success) {
        setResultadoUnion(res.data);
        setTorneos(res.data.torneos || []);
        setHasFilters(true);
        setModoVistaJugador(false);
        toast.success(`Union ${busquedaUnionId}: ${res.data.total_jugadores_activos} jugadores activos en ${res.data.total_clubs} clubs`);
      }
    } catch (e) { toast.error('Error al buscar union: ' + (e.response?.data?.detail || e.message)); }
    finally { setLoadingBusqueda(false); }
  };

  const exportarIdsJugadores = async (tipo, idValor) => {
    try {
      const token = localStorage.getItem('token');
      const params = { tipo, id_valor: idValor };
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      const res = await axios.get(`${API}/superunion/ppst/exportar-ids-jugadores`, {
        headers: { Authorization: `Bearer ${token}` }, params, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `jugadores_${tipo}_${idValor}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel descargado');
    } catch (e) { toast.error('Error al exportar'); }
  };

  const buscarPorJugadorId = async () => {
    if (!busquedaJugadorId.trim()) {
      toast.error('Ingrese un ID de jugador');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Construir parámetros respetando los filtros de fecha
      const params = {
        player_id: busquedaJugadorId.trim()
      };
      
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      
      const response = await axios.get(
        `${API}/superunion/ppst/buscar-jugador`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: params
        }
      );
      
      if (response.data.success && response.data.torneos && response.data.torneos.length > 0) {
        setTorneos(response.data.torneos);
        setDetallesJugador(response.data.detalles_jugador || []);
        setModoVistaJugador(true);
        setHasFilters(true);
        toast.success(`Se encontraron ${response.data.torneos.length} torneo(s) del jugador ${busquedaJugadorId}`);
      } else {
        setTorneos([]);
        setDetallesJugador([]);
        setModoVistaJugador(false);
        setHasFilters(false);
        toast.info('No se encontraron torneos para este jugador en el rango de fechas seleccionado');
      }
    } catch (error) {
      console.error('Error buscando jugador:', error);
      toast.error('Error al buscar torneos del jugador');
    } finally {
      setLoading(false);
    }
  };

  const verDetalleTorneo = async (torneo) => {
    setTorneoSeleccionado(torneo);
    setShowTorneoModal(true);
    setLoadingTorneo(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/superunion/ppst/torneo/${torneo.partida_id}/jugadores`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setJugadoresTorneo(response.data.jugadores || []);
      }
    } catch (error) {
      console.error('Error cargando jugadores:', error);
      toast.error('Error al cargar jugadores del torneo');
    } finally {
      setLoadingTorneo(false);
    }
  };

  const verDesgloseTorneo = async (torneo) => {
    setTorneoDesglose(torneo);
    setShowDesgloseModal(true);
    setLoadingDesglose(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/superunion/ppst/torneo/${torneo.partida_id}/jugadores`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const jugadores = response.data.jugadores || [];
        setJugadoresDesglose(jugadores);
        
        // Calcular desglose por unión
        const unionMap = {};
        
        jugadores.forEach(j => {
          const unionId = j.union_id || 'Sin Unión';
          const unionNombre = j.union_nombre || unionId;
          
          if (!unionMap[unionId]) {
            unionMap[unionId] = {
              union_id: unionId,
              union_nombre: unionNombre,
              total_entradas: 0,
              total_jugadores: 0,
              jugadores: []
            };
          }
          
          unionMap[unionId].total_entradas += 1;
          
          // Contar jugadores únicos
          if (!unionMap[unionId].jugadores.includes(j.jugador_id)) {
            unionMap[unionId].jugadores.push(j.jugador_id);
            unionMap[unionId].total_jugadores += 1;
          }
        });
        
        // Convertir a array y ordenar por total_entradas
        const desgloseArray = Object.values(unionMap).sort((a, b) => b.total_entradas - a.total_entradas);
        setDesglosePorUnion(desgloseArray);
      }
    } catch (error) {
      console.error('Error cargando desglose:', error);
      toast.error('Error al cargar desglose del torneo');
    } finally {
      setLoadingDesglose(false);
    }
  };
  
  const realizarAnalisisComparativo = async () => {
    if (!periodo1Desde || !periodo1Hasta || !periodo2Desde || !periodo2Hasta) {
      toast.error('Por favor completa ambos periodos de fechas');
      return;
    }
    
    setLoadingComparativo(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Cargar datos del Periodo 1
      const params1 = new URLSearchParams();
      params1.append('fecha_desde', periodo1Desde);
      params1.append('fecha_hasta', periodo1Hasta);
      params1.append('limit', '2000');
      if (!incluirSpins) params1.append('exclude_spinup', 'true');
      
      const response1 = await axios.get(
        `${API}/superunion/ppst/torneos?${params1.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Cargar datos del Periodo 2
      const params2 = new URLSearchParams();
      params2.append('fecha_desde', periodo2Desde);
      params2.append('fecha_hasta', periodo2Hasta);
      params2.append('limit', '2000');
      if (!incluirSpins) params2.append('exclude_spinup', 'true');
      
      const response2 = await axios.get(
        `${API}/superunion/ppst/torneos?${params2.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const torneos1 = response1.data.torneos || [];
      const torneos2 = response2.data.torneos || [];
      
      // Calcular métricas para ambos periodos
      const calcularMetricas = (torneos) => ({
        total_torneos: torneos.length,
        total_jugadores: torneos.reduce((sum, t) => sum + (t.total_jugadores || 0), 0),
        overlay_total: torneos.reduce((sum, t) => sum + (t.overlay || 0), 0),
        overlay_promedio: torneos.length > 0 
          ? torneos.reduce((sum, t) => sum + (t.overlay || 0), 0) / torneos.length 
          : 0,
        buy_in_promedio: torneos.length > 0
          ? torneos.reduce((sum, t) => sum + (t.buy_in || 0), 0) / torneos.length
          : 0,
        jugadores_promedio: torneos.length > 0
          ? torneos.reduce((sum, t) => sum + (t.total_jugadores || 0), 0) / torneos.length
          : 0
      });
      
      const metricas1 = calcularMetricas(torneos1);
      const metricas2 = calcularMetricas(torneos2);
      
      // Calcular diferencias
      const calcularDiferencia = (val1, val2) => {
        if (val1 === 0) return val2 === 0 ? 0 : 100;
        return ((val2 - val1) / Math.abs(val1)) * 100;
      };
      
      setDatosComparativos({
        periodo1: { fechas: `${periodo1Desde} a ${periodo1Hasta}`, metricas: metricas1 },
        periodo2: { fechas: `${periodo2Desde} a ${periodo2Hasta}`, metricas: metricas2 },
        diferencias: {
          torneos: calcularDiferencia(metricas1.total_torneos, metricas2.total_torneos),
          jugadores: calcularDiferencia(metricas1.total_jugadores, metricas2.total_jugadores),
          overlay_total: calcularDiferencia(metricas1.overlay_total, metricas2.overlay_total),
          overlay_promedio: calcularDiferencia(metricas1.overlay_promedio, metricas2.overlay_promedio),
          buy_in_promedio: calcularDiferencia(metricas1.buy_in_promedio, metricas2.buy_in_promedio),
          jugadores_promedio: calcularDiferencia(metricas1.jugadores_promedio, metricas2.jugadores_promedio)
        }
      });
      
      toast.success('Análisis comparativo generado');
      
    } catch (error) {
      console.error('Error en análisis comparativo:', error);
      toast.error('Error al generar análisis comparativo');
    } finally {
      setLoadingComparativo(false);
    }
  };

  const calcularEstadisticas = () => {
    if (torneos.length === 0) {
      toast.error('No hay datos para calcular estadísticas');
      return;
    }
    
    console.log('📊 Calculando estadísticas para', torneos.length, 'torneos');
    
    // Calcular SOLO overlay negativo (déficit garantizado)
    const overlay_total = torneos.reduce((sum, t) => {
      const overlay = t.overlay || 0;
      // Solo sumar si es negativo (déficit)
      if (overlay < 0) {
        return sum + overlay;
      }
      return sum;
    }, 0);
    
    console.log('💰 Déficit garantizado calculado:', overlay_total);
    
    // Calcular estadísticas
    const stats = {
      total_torneos: torneos.length,
      total_jugadores: torneos.reduce((sum, t) => sum + (t.total_jugadores || 0), 0),
      overlay_total: overlay_total, // Solo déficit (negativos)
      buy_in_promedio: torneos.reduce((sum, t) => sum + (t.buy_in || 0), 0) / torneos.length,
      
      // Por horario
      por_horario: {},
      
      // Por modalidad
      por_modalidad: {},
      
      // Por día de semana
      por_dia_semana: {},
      
      // Torneos con más overlay
      top_overlay: torneos
        .filter(t => t.overlay < 0)
        .sort((a, b) => a.overlay - b.overlay)
        .slice(0, 5)
    };
    
    console.log('Total jugadores calculado:', stats.total_jugadores);
    
    // Agrupar por horario (5 rangos) - CONVERTIR A HORA ARGENTINA (UTC-5 → ART UTC-3 = +2 horas)
    torneos.forEach(t => {
      // Extraer hora del partida_id (formato: DDMMYYHHMMSS-XXXXXXXX)
      let hora = null;
      if (t.partida_id) {
        const match = t.partida_id.match(/^\d{6}(\d{2})\d{4}-/);
        if (match) {
          hora = parseInt(match[1]);
          // Convertir de UTC-5 a hora Argentina (UTC-3) = +2 horas
          hora = (hora + 2) % 24;
        }
      }
      
      if (hora !== null) {
        // Determinar rango horario (en hora Argentina)
        let rango = '';
        if (hora >= 0 && hora < 8) {
          rango = '🌙 Madrugada (00:00 - 08:00)';
        } else if (hora >= 8 && hora < 12) {
          rango = '☀️ Mañana (08:00 - 12:00)';
        } else if (hora >= 12 && hora < 16) {
          rango = '🌤️ Tarde (12:00 - 16:00)';
        } else if (hora >= 16 && hora < 20) {
          rango = '🌆 Noche (16:00 - 20:00)';
        } else {
          rango = '🌃 Noche Tardía (20:00 - 00:00)';
        }
        
        if (!stats.por_horario[rango]) {
          stats.por_horario[rango] = { 
            count: 0, 
            overlay_promedio: 0, 
            jugadores_promedio: 0,
            total_jugadores: 0 
          };
        }
        stats.por_horario[rango].count++;
        stats.por_horario[rango].overlay_promedio += (t.overlay || 0);
        stats.por_horario[rango].jugadores_promedio += (t.total_jugadores || 0);
        stats.por_horario[rango].total_jugadores += (t.total_jugadores || 0);
      }
      
      // Agrupar por modalidad
      if (t.modalidad) {
        if (!stats.por_modalidad[t.modalidad]) {
          stats.por_modalidad[t.modalidad] = { count: 0, overlay_promedio: 0 };
        }
        stats.por_modalidad[t.modalidad].count++;
        stats.por_modalidad[t.modalidad].overlay_promedio += (t.overlay || 0);
      }
      
      // Agrupar por día de semana
      if (t.fecha_importacion) {
        const dia = new Date(t.fecha_importacion).toLocaleDateString('es-ES', { weekday: 'long' });
        if (!stats.por_dia_semana[dia]) {
          stats.por_dia_semana[dia] = { count: 0, overlay_promedio: 0 };
        }
        stats.por_dia_semana[dia].count++;
        stats.por_dia_semana[dia].overlay_promedio += (t.overlay || 0);
      }
    });
    
    // Promediar
    Object.keys(stats.por_horario).forEach(h => {
      stats.por_horario[h].overlay_promedio /= stats.por_horario[h].count;
      stats.por_horario[h].jugadores_promedio /= stats.por_horario[h].count;
    });
    
    Object.keys(stats.por_modalidad).forEach(m => {
      stats.por_modalidad[m].overlay_promedio /= stats.por_modalidad[m].count;
    });
    
    Object.keys(stats.por_dia_semana).forEach(d => {
      stats.por_dia_semana[d].overlay_promedio /= stats.por_dia_semana[d].count;
    });
    
    setEstadisticas(stats);
    setMostrarEstadisticas(true);
  };

  const exportarCSV = () => {
    if (torneos.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }
    
    // Generar CSV
    const headers = ['Fecha', 'ID Partida', 'Nombre Torneo', 'Buy-in', 'GTD', 'Jugadores', 'Overlay', 'Modalidad', 'Club'];
    const rows = torneos.map(t => [
      t.fecha_snapshot || '',
      t.partida_id || '',
      t.nombre_torneo || t.nombre_mesa || '',
      t.buy_in || '',
      t.gtd || '',
      t.total_jugadores || '',
      t.overlay || '',
      t.modalidad || '',
      t.club_nombre || ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    // Descargar
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `ppst_informe_${fechaDesde}_${fechaHasta}.csv`;
    link.click();
    
    toast.success('Archivo CSV descargado');
  };

  const exportarJugadorExcel = async () => {
    if (detallesJugador.length === 0) {
      toast.error('No hay datos del jugador para exportar');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        player_id: busquedaJugadorId.trim(),
        ratio: ratio
      });
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);
      
      toast.info('Generando Excel profesional...');
      
      const response = await axios.get(
        `${API}/superunion/ppst/exportar-jugador-excel?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Descargar el archivo
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ratioSafe = ratio.replace(':', 'a');
      link.setAttribute('download', `jugador_${busquedaJugadorId}_${ratioSafe}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel descargado correctamente');
    } catch (error) {
      console.error('Error exportando a Excel:', error);
      toast.error('Error al exportar a Excel');
    }
  };

  // Filtrar jugadores en modal
  const jugadoresFiltrados = jugadoresTorneo.filter(j => {
    if (filtroClubTorneo && j.club_nombre && !j.club_nombre.toLowerCase().includes(filtroClubTorneo.toLowerCase())) {
      return false;
    }
    if (filtroJugadorTorneo && j.apodo && !j.apodo.toLowerCase().includes(filtroJugadorTorneo.toLowerCase())) {
      return false;
    }
    return true;
  });



  // ============== CONTROL BOT PPST ==============

  const ejecutarControlBotPPST = async () => {
    setLoadingControlBotPPST(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // 1. Obtener IDs ganadores nuevos PPST (no en historial)
      const response = await axios.get(
        `${API}/control-bot-ppst/ganadores-nuevos`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { ids_nuevos, total_ganadores, ya_enviados, pendientes, exportando } = response.data;
      
      if (ids_nuevos.length === 0) {
        toast.info('✅ No hay IDs ganadores PPST nuevos. Todos ya fueron enviados.');
        setLoadingControlBotPPST(false);
        return;
      }
      
      // ALERTA: Si hay más de 2000 IDs pendientes
      if (pendientes > 2000) {
        toast.warning(`⚠️ Atención: Hay ${pendientes} IDs PPST pendientes. Exportando los primeros 2000.`);
      }
      
      // 2. Crear Excel con UNA SOLA COLUMNA (solo IDs)
      const datosExcel = ids_nuevos.map((id) => ({
        'Jugador ID': id
      }));
      
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(datosExcel);
      ws['!cols'] = [{ wch: 15 }];
      XLSX.utils.book_append_sheet(wb, ws, 'IDs Ganadores PPST');
      
      // Nombre de archivo con fecha actual para control
      const fechaHoy = new Date().toISOString().split('T')[0];
      const nombreArchivo = `control_bot_ids_ganadores_ppst_${fechaHoy}.xlsx`;
      XLSX.writeFile(wb, nombreArchivo);
      
      // 3. Marcar IDs como enviados en el historial
      await axios.post(
        `${API}/control-bot-ppst/marcar-enviados`,
        { ids: ids_nuevos },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(
        `🏆 Control Bot PPST: ${exportando} IDs ganadores exportados\n` +
        `Total ganadores: ${total_ganadores} | Ya enviados: ${ya_enviados} | Pendientes: ${pendientes}`
      );
      
      console.log(`🏆 Control Bot PPST ejecutado: ${exportando} IDs exportados de ${pendientes} pendientes (Total histórico: ${total_ganadores} ganadores)`);
      
    } catch (error) {
      console.error('Error en Control Bot PPST:', error);
      toast.error('Error ejecutando Control Bot PPST: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingControlBotPPST(false);
    }
  };

  const resetHistorialControlBotPPST = async () => {
    if (!window.confirm('¿Estás seguro de resetear el historial PPST? Esto hará que todos los IDs se exporten de nuevo.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API}/control-bot-ppst/reset-historial`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('✅ Historial PPST reseteado correctamente');
      console.log('🏆 Control Bot PPST: Historial reseteado');
      
    } catch (error) {
      console.error('Error reseteando historial PPST:', error);
      toast.error('Error reseteando historial PPST');
    }
  };



  // ============== ANÁLISIS DE RESULTADOS BOT PPST ==============

  // Cargar historial al montar
  useEffect(() => {
    cargarHistorialBotPPST();
    
    // Configurar fechas por defecto (semana anterior)
    const today = new Date();
    const lastMonday = new Date(today);
    lastMonday.setDate(today.getDate() - today.getDay() - 6);
    const lastSunday = new Date(today);
    lastSunday.setDate(today.getDate() - today.getDay());
    
    setSemanaInicioBotPPST(lastMonday.toISOString().split('T')[0]);
    setSemanaFinBotPPST(lastSunday.toISOString().split('T')[0]);
  }, []);

  const cargarHistorialBotPPST = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/bot-resultados-ppst/historial`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setHistorialInformesPPST(response.data);
    } catch (error) {
      console.error('Error cargando historial PPST:', error);
    }
  };

  const handleArchivoResultadosPPSTChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setArchivoResultadosPPST(file);
    }
  };

  const procesarResultadosBotPPST = async () => {
    if (!archivoResultadosPPST) {
      toast.error('Por favor selecciona un archivo Excel');
      return;
    }

    if (!semanaInicioBotPPST || !semanaFinBotPPST) {
      toast.error('Por favor selecciona las fechas de inicio y fin');
      return;
    }

    setLoadingProcesarPPST(true);

    try {
      const token = localStorage.getItem('token');
      
      const formData = new FormData();
      formData.append('file', archivoResultadosPPST);
      formData.append('semana_inicio', semanaInicioBotPPST);
      formData.append('semana_fin', semanaFinBotPPST);
      formData.append('incluir_perdedores', incluyePerdedoresPPST);

      const response = await axios.post(
        `${API}/bot-resultados-ppst/procesar`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        console.log('✅ [PPST] Informes generados:', response.data.archivos);
        
        // Recargar historial
        await cargarHistorialBotPPST();
        
        // Limpiar archivo
        setArchivoResultadosPPST(null);
        document.getElementById('file-resultados-bot-ppst').value = '';
      }
    } catch (error) {
      console.error('Error procesando resultados PPST:', error);
      toast.error('Error procesando resultados: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingProcesarPPST(false);
    }
  };

  const descargarInformePPST = async (historialId, union) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${API}/bot-resultados-ppst/descargar/${historialId}/${union}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `informe_bot_ppst_${union}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      
      toast.success(`✅ Informe de unión ${union} descargado`);
    } catch (error) {
      console.error('Error descargando informe PPST:', error);
      toast.error('Error descargando informe');
    }
  };

  const eliminarInformePPST = async (historialId) => {
    if (!window.confirm('¿Estás seguro de eliminar este informe?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      await axios.delete(
        `${API}/bot-resultados-ppst/historial/${historialId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('✅ Informe eliminado');
      await cargarHistorialBotPPST();
    } catch (error) {
      console.error('Error eliminando informe PPST:', error);
      toast.error('Error eliminando informe');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Trophy className="w-8 h-8 text-purple-600" />
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                🏆 PPST - Informe de Torneos
              </h1>
              
              {/* Selector de Ratio */}
              <div className="ml-4 flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Ratio:</span>
                <select
                  value={ratio}
                  onChange={(e) => setRatio(e.target.value)}
                  className="px-3 py-1 border-2 border-purple-300 dark:border-purple-600 rounded-lg text-sm font-semibold bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:border-purple-500 transition-colors"
                >
                  <option value="1:6">1:6 (Original)</option>
                  <option value="1:1">1:1 (÷6)</option>
                  <option value="1:5">1:5 (÷6×5)</option>
                </select>
              </div>
            </div>
            <button
              onClick={calcularEstadisticas}
              disabled={torneos.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:bg-gray-400"
            >
              <TrendingUp className="w-4 h-4" />
              Estadísticas
            </button>
          </div>
          
          {snapshots.length === 0 && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <div className="flex">
                <AlertTriangle className="w-5 h-5 text-yellow-400 mr-3" />
                <div>
                  <p className="text-sm text-yellow-700">
                    <strong>⚠️ No hay datos de torneos (PPST) cargados.</strong>
                  </p>
                  <p className="text-sm text-yellow-700 mt-1">
                    Ve a <strong>Consolidación</strong> para cargar archivos PPST y comenzar a visualizar los torneos.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <p className="text-gray-600 dark:text-gray-400">
            Consulta y analiza todos los eventos de torneos. {snapshots.length} snapshots PPST disponibles.
          </p>
        </div>

        {/* Filtros */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
            <Search className="w-5 h-5" />
            Filtros de Búsqueda
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Rango de Fechas */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Fecha Desde
              </label>
              <input
                type="date"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Fecha Hasta
              </label>
              <input
                type="date"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            
            {/* Unión */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Unión
              </label>
              <select
                value={selectedUnionId}
                onChange={(e) => {
                  setSelectedUnionId(e.target.value);
                  setSelectedClubId(''); // Reset club cuando cambia unión
                }}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                disabled={!fechaDesde && !fechaHasta}
              >
                <option value="">Todas las uniones</option>
                {allUniones.map(u => (
                  <option key={u.union_id} value={u.union_id}>
                    Unión {u.union_id} ({u.total_jugadores} jugadores)
                  </option>
                ))}
              </select>
              {(!fechaDesde && !fechaHasta) && (
                <p className="text-xs text-gray-500 mt-1">Selecciona un rango de fechas primero</p>
              )}
            </div>
            
            {/* Club */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Club
              </label>
              <select
                value={selectedClubId}
                onChange={(e) => setSelectedClubId(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                disabled={!selectedUnionId}
              >
                <option value="">Todos los clubs</option>
                {allClubs.map(c => (
                  <option key={c.club_id} value={c.club_id}>
                    {c.club_name || `Club ${c.club_id}`} ({c.total_jugadores} jugadores)
                  </option>
                ))}
              </select>
              {!selectedUnionId && (
                <p className="text-xs text-gray-500 mt-1">Selecciona una unión primero</p>
              )}
            </div>
            
            {/* Modalidad */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Modalidad
              </label>
              <select
                value={selectedModalidad}
                onChange={(e) => setSelectedModalidad(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="">Todas</option>
                {modalidades.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            
            {/* Buy-in */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Buy-in Mín - Máx
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={buyInMin}
                  onChange={(e) => setBuyInMin(e.target.value)}
                  className="w-1/2 px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={buyInMax}
                  onChange={(e) => setBuyInMax(e.target.value)}
                  className="w-1/2 px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
            </div>
            
            {/* Búsqueda */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Buscar (ID partida, nombre torneo, mesa)
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Ej: 251020184714-98323946"
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            
            {/* Ordenar Por */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                📊 Ordenar Por
              </label>
              <select
                value={ordenarPor}
                onChange={(e) => setOrdenarPor(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white font-semibold"
              >
                <option value="fecha">📅 Fecha (más reciente primero)</option>
                <option value="buyin">💰 Buy-in (mayor primero)</option>
                <option value="overlay">📉 Overlay (mayor déficit primero)</option>
                <option value="jugadores">👥 Jugadores (más participación)</option>
                <option value="gtd">🏆 GTD (mayor premio primero)</option>
                <option value="nombre">🔤 Nombre (A-Z)</option>
              </select>
            </div>
            
            {/* Solo con Overlay y Spin & Go */}
            <div className="flex items-center gap-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="soloOverlay"
                  checked={soloConOverlay}
                  onChange={(e) => setSoloConOverlay(e.target.checked)}
                  className="mr-2"
                />
                <label htmlFor="soloOverlay" className="text-sm text-gray-700 dark:text-gray-300">
                  Solo torneos con overlay (déficit)
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="incluirSpins"
                  checked={incluirSpins}
                  onChange={(e) => setIncluirSpins(e.target.checked)}
                  className="mr-2"
                />
                <label htmlFor="incluirSpins" className="text-sm font-medium text-purple-700 dark:text-purple-400">
                  🎰 Incluir Spin & Go (por defecto ocultos)
                </label>
              </div>
            </div>
          </div>
          
          {/* Botones */}
          <div className="flex gap-3 mt-4">
            <button
              onClick={aplicarFiltros}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
            >
              <Search className="w-4 h-4" />
              {loading ? 'Buscando...' : 'Aplicar Filtros'}
            </button>
            
            <button
              onClick={limpiarFiltros}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              <X className="w-4 h-4" />
              Limpiar
            </button>
            
            {torneos.length > 0 && (
              <>
                <button
                  onClick={exportarCSV}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Download className="w-4 h-4" />
                  Exportar CSV
                </button>
                
                <button
                  onClick={() => setMostrarComparativo(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  <TrendingUp className="w-4 h-4" />
                  Análisis Comparativo
                </button>
              </>
            )}
          </div>
        </div>

        {/* Búsqueda Rápida Expandida */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6" data-testid="ppst-busqueda-rapida">
          <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">Busqueda Rapida</h3>
          <div className="flex gap-2 mb-3">
            {['jugador', 'club', 'union'].map(t => (
              <button key={t} onClick={() => setTipoBusqueda(t)}
                className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                  tipoBusqueda === t
                    ? (t === 'jugador' ? 'bg-violet-600 text-white' : t === 'club' ? 'bg-blue-600 text-white' : 'bg-emerald-600 text-white')
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`} data-testid={`busqueda-tipo-${t}`}>
                {t === 'jugador' ? 'ID Jugador' : t === 'club' ? 'ID Club' : 'ID Union'}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            {tipoBusqueda === 'jugador' && (
              <>
                <input type="text" value={busquedaJugadorId} onChange={(e) => setBusquedaJugadorId(e.target.value)}
                  placeholder="Ej: 98323946" className="flex-1 px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                <button onClick={buscarPorJugadorId} disabled={loading}
                  className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 font-semibold">Buscar</button>
              </>
            )}
            {tipoBusqueda === 'club' && (
              <>
                <input type="text" value={busquedaClubId} onChange={(e) => setBusquedaClubId(e.target.value)}
                  placeholder="Ej: 89321" className="flex-1 px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                <button onClick={buscarPorClubId} disabled={loadingBusqueda}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold">
                  {loadingBusqueda ? 'Buscando...' : 'Buscar'}
                </button>
              </>
            )}
            {tipoBusqueda === 'union' && (
              <>
                <input type="text" value={busquedaUnionId} onChange={(e) => setBusquedaUnionId(e.target.value)}
                  placeholder="Ej: 1534" className="flex-1 px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                <button onClick={buscarPorUnionId} disabled={loadingBusqueda}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold">
                  {loadingBusqueda ? 'Buscando...' : 'Buscar'}
                </button>
              </>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            {tipoBusqueda === 'jugador' ? 'Busca todos los torneos donde participo este jugador' :
             tipoBusqueda === 'club' ? 'Muestra jugadores activos del club y sus estadisticas' :
             'Muestra miembros activos de la union desglosados por club'}
            {' '}(respeta filtros de fecha)
          </p>
        </div>

        {/* Resultado Club */}
        {resultadoClub && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded-xl p-4 mb-6 shadow" data-testid="resultado-club">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-blue-800 dark:text-blue-300 text-lg">
                Club: {resultadoClub.nombre_club} (ID: {resultadoClub.club_id})
              </h3>
              <button onClick={() => exportarIdsJugadores('club', resultadoClub.club_id)}
                className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700"
                data-testid="exportar-ids-club">
                <Download className="w-4 h-4" /> Exportar IDs
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center shadow-sm">
                <div className="text-2xl font-bold text-blue-600">{resultadoClub.jugadores_activos}</div>
                <div className="text-xs text-gray-500">Jugadores Activos</div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center shadow-sm">
                <div className="text-2xl font-bold text-purple-600">{resultadoClub.torneos_participados}</div>
                <div className="text-xs text-gray-500">Torneos</div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center shadow-sm">
                <div className={`text-2xl font-bold ${resultadoClub.total_ganancia >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {resultadoClub.total_ganancia?.toLocaleString('es-AR', {minimumFractionDigits: 2})}
                </div>
                <div className="text-xs text-gray-500">Ganancia Total</div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center shadow-sm">
                <div className="text-2xl font-bold text-orange-600">
                  {resultadoClub.total_cuota?.toLocaleString('es-AR', {minimumFractionDigits: 2})}
                </div>
                <div className="text-xs text-gray-500">Cuota Total</div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-blue-100 dark:bg-blue-800">
                    <th className="px-3 py-2 text-left">ID</th>
                    <th className="px-3 py-2 text-left">Apodo</th>
                    <th className="px-3 py-2 text-right">Torneos</th>
                    <th className="px-3 py-2 text-right">Ganancia</th>
                    <th className="px-3 py-2 text-right">Cuota</th>
                    <th className="px-3 py-2 text-right">Mejor Ranking</th>
                  </tr>
                </thead>
                <tbody>
                  {resultadoClub.jugadores?.map((j, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}>
                      <td className="px-3 py-1.5 font-mono font-bold">{j.jugador_id}</td>
                      <td className="px-3 py-1.5">{j.apodo || '-'}</td>
                      <td className="px-3 py-1.5 text-right">{j.torneos_jugados}</td>
                      <td className={`px-3 py-1.5 text-right font-semibold ${j.ganancia_total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {j.ganancia_total?.toLocaleString('es-AR', {minimumFractionDigits: 2})}
                      </td>
                      <td className="px-3 py-1.5 text-right">{j.cuota_total?.toLocaleString('es-AR', {minimumFractionDigits: 2})}</td>
                      <td className="px-3 py-1.5 text-right">{j.mejor_ranking}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Resultado Union */}
        {resultadoUnion && (
          <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 rounded-xl p-4 mb-6 shadow" data-testid="resultado-union">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-emerald-800 dark:text-emerald-300 text-lg">
                Union ID: {resultadoUnion.union_id}
              </h3>
              <button onClick={() => exportarIdsJugadores('union', resultadoUnion.union_id)}
                className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700"
                data-testid="exportar-ids-union">
                <Download className="w-4 h-4" /> Exportar IDs Union
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center shadow-sm">
                <div className="text-2xl font-bold text-emerald-600">{resultadoUnion.total_jugadores_activos}</div>
                <div className="text-xs text-gray-500">Jugadores Activos</div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center shadow-sm">
                <div className="text-2xl font-bold text-blue-600">{resultadoUnion.total_clubs}</div>
                <div className="text-xs text-gray-500">Clubs</div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center shadow-sm">
                <div className="text-2xl font-bold text-purple-600">{resultadoUnion.torneos_participados}</div>
                <div className="text-xs text-gray-500">Torneos</div>
              </div>
              <div className={`bg-white dark:bg-gray-800 rounded-lg p-3 text-center shadow-sm`}>
                <div className={`text-2xl font-bold ${resultadoUnion.total_ganancia >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {resultadoUnion.total_ganancia?.toLocaleString('es-AR', {minimumFractionDigits: 2})}
                </div>
                <div className="text-xs text-gray-500">Ganancia Total</div>
              </div>
            </div>
            <div className="space-y-3">
              {resultadoUnion.clubs?.map((club, ci) => (
                <div key={ci} className="bg-white dark:bg-gray-800 border rounded-lg p-3 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-800 dark:text-white">{club.nombre_club}</span>
                      <span className="text-xs text-gray-500">(ID: {club.club_id})</span>
                      <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-0.5 rounded">
                        {club.jugadores_activos} activos
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <span className={`font-semibold ${club.total_ganancia >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        Gan: {club.total_ganancia?.toLocaleString('es-AR', {minimumFractionDigits: 2})}
                      </span>
                      <button onClick={() => exportarIdsJugadores('club', club.club_id)}
                        className="text-blue-600 hover:text-blue-800 font-semibold text-xs flex items-center gap-1">
                        <Download className="w-3 h-3" /> Excel
                      </button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-gray-700">
                          <th className="px-2 py-1 text-left">ID</th>
                          <th className="px-2 py-1 text-left">Apodo</th>
                          <th className="px-2 py-1 text-right">Torneos</th>
                          <th className="px-2 py-1 text-right">Ganancia</th>
                          <th className="px-2 py-1 text-right">Cuota</th>
                        </tr>
                      </thead>
                      <tbody>
                        {club.jugadores?.slice(0, 20).map((j, ji) => (
                          <tr key={ji} className={ji % 2 === 0 ? '' : 'bg-gray-50 dark:bg-gray-700'}>
                            <td className="px-2 py-1 font-mono font-bold">{j.jugador_id}</td>
                            <td className="px-2 py-1">{j.apodo || '-'}</td>
                            <td className="px-2 py-1 text-right">{j.torneos_jugados}</td>
                            <td className={`px-2 py-1 text-right font-semibold ${j.ganancia_total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {j.ganancia_total?.toLocaleString('es-AR', {minimumFractionDigits: 2})}
                            </td>
                            <td className="px-2 py-1 text-right">{j.cuota_total?.toLocaleString('es-AR', {minimumFractionDigits: 2})}</td>
                          </tr>
                        ))}
                        {club.jugadores?.length > 20 && (
                          <tr><td colSpan="5" className="px-2 py-1 text-center text-gray-500 italic">
                            ...y {club.jugadores.length - 20} jugadores mas (descargar Excel para ver todos)
                          </td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}


        {/* CONTROL BOT PPST (TORNEOS) - Solo admin */}
        {isAdmin && (<div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 rounded-lg border-2 border-green-300 dark:border-green-600 p-4 shadow-lg mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Trophy className="w-6 h-6 text-green-600 dark:text-green-400" />
            <h3 className="font-bold text-green-800 dark:text-green-300">Control Bot PPST (Torneos)</h3>
          </div>
          <p className="text-xs text-green-700 dark:text-green-400 mb-3">
            Exporta IDs ganadores de Torneos no enviados (todos los datos históricos)
          </p>
          <div className="flex gap-2">
            <button
              onClick={ejecutarControlBotPPST}
              disabled={loadingControlBotPPST}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                loadingControlBotPPST
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 text-white shadow-md hover:shadow-lg'
              }`}
            >
              {loadingControlBotPPST ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Procesando...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Ejecutar
                </>
              )}
            </button>
            <button
              onClick={resetHistorialControlBotPPST}
              className="flex items-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
              title="Resetear historial de IDs enviados PPST"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
          </div>
        </div>)}


        {/* ANÁLISIS DE RESULTADOS BOT PPST - Solo admin */}
        {isAdmin && (<div className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/30 dark:to-amber-900/30 rounded-lg border-2 border-orange-300 dark:border-orange-600 p-4 shadow-lg mb-6">
          <div className="flex items-center gap-3 mb-4">
            <FileSpreadsheet className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            <div>
              <h3 className="font-bold text-orange-800 dark:text-orange-300">Análisis de Resultados Bot PPST</h3>
              <p className="text-xs text-orange-700 dark:text-orange-400">
                Procesa respuestas del bot y genera informes de torneos por unión
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Upload Excel */}
            <div>
              <label className="block text-sm font-semibold text-orange-800 dark:text-orange-300 mb-2">
                📤 Archivo de Resultados
              </label>
              <input
                id="file-resultados-bot-ppst"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleArchivoResultadosPPSTChange}
                className="w-full px-3 py-2 border-2 border-orange-300 dark:border-orange-600 rounded-lg text-sm"
              />
              {archivoResultadosPPST && (
                <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                  ✓ {archivoResultadosPPST.name}
                </p>
              )}
            </div>
            
            {/* Fecha Inicio */}
            <div>
              <label className="block text-sm font-semibold text-orange-800 dark:text-orange-300 mb-2">
                📅 Semana Inicio
              </label>
              <input
                type="date"
                value={semanaInicioBotPPST}
                onChange={(e) => setSemanaInicioBotPPST(e.target.value)}
                className="w-full px-3 py-2 border-2 border-orange-300 dark:border-orange-600 rounded-lg"
              />
            </div>
            
            {/* Fecha Fin */}
            <div>
              <label className="block text-sm font-semibold text-orange-800 dark:text-orange-300 mb-2">
                📅 Semana Fin
              </label>
              <input
                type="date"
                value={semanaFinBotPPST}
                onChange={(e) => setSemanaFinBotPPST(e.target.value)}
                className="w-full px-3 py-2 border-2 border-orange-300 dark:border-orange-600 rounded-lg"
              />
            </div>
          </div>
          
          {/* Checkbox Incluir Perdedores */}
          <div className="mt-3 flex items-center gap-3">
            <input
              type="checkbox"
              id="incluir-perdedores-ppst"
              checked={incluyePerdedoresPPST}
              onChange={(e) => setIncluyePerdedoresPPST(e.target.checked)}
              className="w-5 h-5 text-orange-600 border-orange-300 rounded focus:ring-orange-500 cursor-pointer"
            />
            <label
              htmlFor="incluir-perdedores-ppst"
              className="text-sm font-semibold text-orange-800 dark:text-orange-300 cursor-pointer select-none"
            >
              📊 Incluir también jugadores con pérdidas (total negativo)
            </label>
          </div>
          {incluyePerdedoresPPST && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 ml-8">
              ⚠️ Se incluirán TODOS los jugadores detectados, incluso los que perdieron dinero
            </p>
          )}
          
          <button
            onClick={procesarResultadosBotPPST}
            disabled={loadingProcesarPPST || !archivoResultadosPPST}
            className={`mt-4 w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-bold transition-all ${
              loadingProcesarPPST || !archivoResultadosPPST
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-orange-600 hover:bg-orange-700 text-white shadow-lg hover:shadow-xl'
            }`}
          >
            {loadingProcesarPPST ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Procesando...
              </>
            ) : (
              <>
                <FileSpreadsheet className="w-5 h-5" />
                Procesar y Generar Informes
              </>
            )}
          </button>
        </div>)}

        {/* HISTORIAL DE INFORMES GENERADOS PPST */}
        {historialInformesPPST.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4">
              📊 Historial de Informes PPST Generados
            </h3>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-orange-100 dark:bg-orange-900/30 border-b-2 border-orange-300 dark:border-orange-700">
                    <th className="px-4 py-3 text-left font-bold text-orange-800 dark:text-orange-300">Fecha</th>
                    <th className="px-4 py-3 text-left font-bold text-orange-800 dark:text-orange-300">Período</th>
                    <th className="px-4 py-3 text-center font-bold text-orange-800 dark:text-orange-300">IDs Analizados</th>
                    <th className="px-4 py-3 text-center font-bold text-orange-800 dark:text-orange-300">Positivos</th>
                    <th className="px-4 py-3 text-center font-bold text-orange-800 dark:text-orange-300">Con Ganancia</th>
                    <th className="px-4 py-3 text-left font-bold text-orange-800 dark:text-orange-300">Uniones</th>
                    <th className="px-4 py-3 text-center font-bold text-orange-800 dark:text-orange-300">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {historialInformesPPST.map((hist, idx) => (
                    <tr key={idx} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        {new Date(hist.fecha_generacion).toLocaleString('es-AR')}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        {hist.semana_inicio} al {hist.semana_fin}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-700 dark:text-gray-300">
                        {hist.total_ids_analizados}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded font-semibold">
                          {hist.ids_positivos}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded font-semibold">
                          {hist.jugadores_con_ganancia}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {hist.archivos.map((archivo, i) => (
                            <button
                              key={i}
                              onClick={() => descargarInformePPST(hist.historial_id, archivo.union)}
                              className="px-2 py-1 bg-orange-600 hover:bg-orange-700 text-white rounded text-xs font-semibold transition-colors"
                              title={`Descargar unión ${archivo.union} (${archivo.count} registros, ${archivo.jugadores_unicos} jugadores)`}
                            >
                              📥 {archivo.union}
                            </button>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => eliminarInformePPST(hist.historial_id)}
                          className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-semibold transition-colors"
                          title="Eliminar informe"
                        >
                          🗑️ Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}



        {/* Resultados */}
        {hasFilters && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {modoVistaJugador ? `Desempeño del Jugador ${busquedaJugadorId}` : `Resultados: ${torneos.length} torneos`}
                  </h2>
                  <div className="flex items-center gap-3">
                    {modoVistaJugador && (
                      <button
                        onClick={exportarJugadorExcel}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <FileSpreadsheet className="w-4 h-4" />
                        Exportar Excel
                      </button>
                    )}
                    {!incluirSpins && !modoVistaJugador && (
                      <div className="flex items-center gap-2 text-sm text-purple-600 dark:text-purple-400">
                        <span className="font-medium">🎰 Spin & Go ocultos</span>
                        <button
                          onClick={() => setIncluirSpins(true)}
                          className="text-xs underline hover:text-purple-800"
                        >
                          Mostrar
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                
                {torneos.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <Trophy className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>No se encontraron torneos con los filtros seleccionados</p>
                  </div>
                ) : modoVistaJugador ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Fecha</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Torneo</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">Ranking</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700 dark:text-gray-300">Entrada</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700 dark:text-gray-300">Cuota</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700 dark:text-gray-300">Ganancia</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-blue-700 dark:text-blue-300">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {detallesJugador.map((detalle, idx) => {
                          const ganancia = detalle.ganancia || 0;
                          const cuota = detalle.cuota || 0;
                          const total = ganancia + cuota;
                          
                          return (
                            <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                {detalle.fecha || '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                {detalle.nombre_mesa || '-'}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                                  detalle.ranking <= 3 
                                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                    : detalle.ranking <= 10
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                }`}>
                                  {detalle.ranking || '-'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right text-sm text-gray-700 dark:text-gray-300">
                                {aplicarRatio(detalle.entrada_fichas || 0).toFixed(2)}
                              </td>
                              <td className="px-4 py-3 text-right text-sm text-gray-700 dark:text-gray-300">
                                {aplicarRatio(cuota).toFixed(2)}
                              </td>
                              <td className={`px-4 py-3 text-right text-sm font-semibold ${
                                ganancia > 0 
                                  ? 'text-green-600 dark:text-green-400'
                                  : 'text-gray-500 dark:text-gray-400'
                              }`}>
                                {aplicarRatio(ganancia).toFixed(2)}
                              </td>
                              <td className={`px-4 py-3 text-right text-sm font-bold ${
                                total > 0
                                  ? 'text-green-600 dark:text-green-400'
                                  : total < 0
                                  ? 'text-red-600 dark:text-red-400'
                                  : 'text-gray-500 dark:text-gray-400'
                              }`}>
                                {aplicarRatio(total).toFixed(2)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-gray-100 dark:bg-gray-700">
                        <tr>
                          <td colSpan="3" className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white">
                            TOTALES
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-bold text-gray-900 dark:text-white">
                            {aplicarRatio(detallesJugador.reduce((sum, d) => sum + (d.entrada_fichas || 0), 0)).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-bold text-gray-900 dark:text-white">
                            {aplicarRatio(detallesJugador.reduce((sum, d) => sum + (d.cuota || 0), 0)).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-bold text-gray-900 dark:text-white">
                            {aplicarRatio(detallesJugador.reduce((sum, d) => sum + (d.ganancia || 0), 0)).toFixed(2)}
                          </td>
                          <td className={`px-4 py-3 text-right text-sm font-bold ${
                            detallesJugador.reduce((sum, d) => sum + (d.ganancia || 0) + (d.cuota || 0), 0) > 0
                              ? 'text-green-600 dark:text-green-400'
                              : detallesJugador.reduce((sum, d) => sum + (d.ganancia || 0) + (d.cuota || 0), 0) < 0
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-gray-900 dark:text-white'
                          }`}>
                            {aplicarRatio(detallesJugador.reduce((sum, d) => sum + (d.ganancia || 0) + (d.cuota || 0), 0)).toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100 dark:bg-gray-700">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Fecha</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">ID Partida</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Torneo</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Buy-in</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">GTD</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Jugadores</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Overlay</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Ticket</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {torneos.map((torneo, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                              {torneo.fecha || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm font-mono text-gray-700 dark:text-gray-300">
                              {torneo.partida_id || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                              <button
                                onClick={() => verDesgloseTorneo(torneo)}
                                className="text-left hover:text-blue-600 hover:underline transition-colors font-medium"
                              >
                                {torneo.nombre_mesa || '-'}
                              </button>
                              
                              {/* Badge de Modalidad */}
                              {torneo.modalidad && (
                            <span className={`ml-2 px-2 py-1 text-xs font-bold rounded inline-flex items-center gap-1 ${
                              torneo.modalidad === 'SPINUP' 
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' 
                                : torneo.modalidad === 'NLH'
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                : (torneo.modalidad === 'PLO' || torneo.modalidad === 'PLO5')
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : torneo.modalidad === 'AOF'
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                              {torneo.modalidad === 'SPINUP' && '🎰'}
                              {torneo.modalidad === 'NLH' && '♠️'}
                              {(torneo.modalidad === 'PLO' || torneo.modalidad === 'PLO5') && '♣️'}
                              {torneo.modalidad === 'AOF' && '⚡'}
                              {torneo.modalidad}
                            </span>
                          )}
                          
                          {/* Badge adicional de KO */}
                          {torneo.nombre_mesa && torneo.nombre_mesa.toUpperCase().includes('KO') && (
                            <span className="ml-1 px-2 py-1 text-xs font-bold rounded inline-flex items-center gap-1 bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">
                              💥 KO
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {torneo.buy_in ? `${aplicarRatio(torneo.buy_in).toFixed(2)}` : (torneo.modalidad === 'SPINUP' ? 'Variable' : '-')}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {torneo.gtd ? `${torneo.gtd}` : (torneo.modalidad === 'SPINUP' ? 'Aleatorio' : '-')}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {torneo.total_jugadores || 0}
                        </td>
                        <td className={`px-4 py-3 text-sm font-semibold ${
                          torneo.modalidad === 'SPINUP' ? 'text-gray-400' :
                          (torneo.overlay || 0) < 0 ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {torneo.modalidad === 'SPINUP' ? 'N/A' : `${aplicarRatio(torneo.overlay || 0).toFixed(2)}`}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {torneo.tiene_tickets ? '✓' : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => verDetalleTorneo(torneo)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Ver Detalle
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Modal de Detalle de Torneo */}
        {showTorneoModal && torneoSeleccionado && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Detalle del Torneo
                </h2>
                <button
                  onClick={() => setShowTorneoModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                {/* Información del Torneo */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">ID Partida</p>
                      <p className="font-semibold text-gray-900 dark:text-white font-mono">
                        {torneoSeleccionado.partida_id || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Torneo</p>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {torneoSeleccionado.nombre_mesa || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Buy-in</p>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {torneoSeleccionado.buy_in ? aplicarRatio(torneoSeleccionado.buy_in).toFixed(2) : '0.00'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">GTD</p>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {torneoSeleccionado.gtd || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Jugadores</p>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {torneoSeleccionado.total_jugadores || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Overlay</p>
                      <p className={`font-semibold ${
                        (torneoSeleccionado.overlay || 0) < 0 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {aplicarRatio(torneoSeleccionado.overlay || 0).toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Modalidad</p>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {torneoSeleccionado.modalidad || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Ticket</p>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {torneoSeleccionado.tiene_tickets ? 'Sí' : 'No'}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Filtros de jugadores */}
                <div className="mb-4 flex gap-3">
                  <input
                    type="text"
                    placeholder="Filtrar por club..."
                    value={filtroClubTorneo}
                    onChange={(e) => setFiltroClubTorneo(e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                  <input
                    type="text"
                    placeholder="Filtrar por jugador..."
                    value={filtroJugadorTorneo}
                    onChange={(e) => setFiltroJugadorTorneo(e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                
                {/* Tabla de Jugadores */}
                {loadingTorneo ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">Cargando jugadores...</p>
                  </div>
                ) : jugadoresFiltrados.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <p>No hay jugadores para este torneo</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100 dark:bg-gray-700">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Club</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Jugador</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Ranking</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Entrada</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Ganancia</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Cuota</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {jugadoresFiltrados.map((jugador, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-4 py-3 text-sm">{jugador.club_nombre || '-'}</td>
                            <td className="px-4 py-3 text-sm font-medium">{jugador.apodo || '-'}</td>
                            <td className="px-4 py-3 text-sm">{jugador.ranking || '-'}</td>
                            <td className="px-4 py-3 text-sm">{aplicarRatio(jugador.entrada || 0).toFixed(2)}</td>
                            <td className={`px-4 py-3 text-sm font-semibold ${
                              (jugador.ganancia || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {aplicarRatio(jugador.ganancia || 0).toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-sm">{aplicarRatio(jugador.cuota || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                <button
                  onClick={() => setShowTorneoModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Estadísticas */}
        {mostrarEstadisticas && estadisticas && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <TrendingUp className="w-6 h-6" />
                  Estadísticas y Análisis
                </h2>
                <button
                  onClick={() => setMostrarEstadisticas(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                {/* Resumen General */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Torneos</p>
                    <p className="text-2xl font-bold text-blue-600">{estadisticas.total_torneos}</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Jugadores</p>
                    <p className="text-2xl font-bold text-green-600">{estadisticas.total_jugadores}</p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Déficit Garantizado</p>
                    <p className="text-2xl font-bold text-red-600">
                      {aplicarRatio(estadisticas.overlay_total).toFixed(2)}
                    </p>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Buy-in Promedio</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {aplicarRatio(estadisticas.buy_in_promedio).toFixed(2)}
                    </p>
                  </div>
                </div>
                
                {/* Por Horario */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Análisis por Horario (5 Rangos)
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100 dark:bg-gray-700">
                        <tr>
                          <th className="px-4 py-2 text-left text-sm">Horario</th>
                          <th className="px-4 py-2 text-left text-sm">Torneos</th>
                          <th className="px-4 py-2 text-left text-sm">Total Jugadores</th>
                          <th className="px-4 py-2 text-left text-sm">Jugadores Prom.</th>
                          <th className="px-4 py-2 text-left text-sm">Overlay Prom.</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {Object.entries(estadisticas.por_horario)
                          .sort(([a], [b]) => {
                            // Ordenar por hora de inicio del rango
                            const horaA = a.includes('Madrugada') ? 0 : a.includes('Mañana') ? 8 : a.includes('Tarde') ? 12 : a.includes('Noche)') ? 16 : 20;
                            const horaB = b.includes('Madrugada') ? 0 : b.includes('Mañana') ? 8 : b.includes('Tarde') ? 12 : b.includes('Noche)') ? 16 : 20;
                            return horaA - horaB;
                          })
                          .map(([horario, data]) => (
                          <tr key={horario} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-4 py-2 text-sm font-medium">{horario}</td>
                            <td className="px-4 py-2 text-sm">{data.count}</td>
                            <td className="px-4 py-2 text-sm font-bold text-blue-600 dark:text-blue-400">
                              {data.total_jugadores}
                            </td>
                            <td className="px-4 py-2 text-sm">{data.jugadores_promedio.toFixed(1)}</td>
                            <td className={`px-4 py-2 text-sm font-semibold ${
                              data.overlay_promedio < 0 ? 'text-red-600' : 'text-green-600'
                            }`}>
                              {aplicarRatio(data.overlay_promedio).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                {/* Por Modalidad */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3">Análisis por Modalidad</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(estadisticas.por_modalidad).map(([modalidad, data]) => (
                      <div key={modalidad} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                        <p className="font-semibold text-gray-900 dark:text-white mb-2">{modalidad}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Torneos: {data.count}
                        </p>
                        <p className={`text-sm font-semibold ${
                          data.overlay_promedio < 0 ? 'text-red-600' : 'text-green-600'
                        }`}>
                          Overlay prom: {aplicarRatio(data.overlay_promedio).toFixed(2)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Top Overlays */}
                {estadisticas.top_overlay.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-red-600">
                      Top 5 Torneos con Mayor Overlay (Déficit)
                    </h3>
                    <div className="space-y-2">
                      {estadisticas.top_overlay.map((torneo, idx) => (
                        <div key={idx} className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg flex justify-between items-center">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {torneo.nombre_torneo || torneo.nombre_mesa}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {torneo.fecha_snapshot} | Buy-in: {torneo.buy_in}
                            </p>
                          </div>
                          <p className="text-xl font-bold text-red-600">
                            {aplicarRatio(torneo.overlay).toFixed(2)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal de Desglose */}
        {showDesgloseModal && torneoDesglose && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gradient-to-r from-purple-600 to-blue-600">
                <h2 className="text-2xl font-bold text-white">
                  🏆 Desglose del Torneo - {torneoDesglose.nombre_mesa}
                </h2>
                <button
                  onClick={() => setShowDesgloseModal(false)}
                  className="text-white hover:text-gray-200"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                {loadingDesglose ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">Cargando desglose...</p>
                  </div>
                ) : jugadoresDesglose.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <p>No hay datos de desglose para este torneo</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* SECCIÓN 1: Información General del Torneo */}
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-700 dark:to-gray-600 rounded-lg p-6 border-l-4 border-blue-600">
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-300">ID Partida</p>
                          <p className="font-semibold text-gray-900 dark:text-white font-mono text-sm">
                            {torneoDesglose.partida_id || '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-300">⏰ Horario (ART)</p>
                          <p className="font-bold text-lg text-purple-600 dark:text-purple-400">
                            {(() => {
                              // Extraer horario del partida_id (formato: DDMMYYHHMMSS-XXXXXXXX)
                              const partidaId = torneoDesglose.partida_id || '';
                              const match = partidaId.match(/^\d{6}(\d{6})-/);
                              if (match) {
                                const timeStr = match[1];
                                let hh = parseInt(timeStr.substring(0, 2));
                                const mm = timeStr.substring(2, 4);
                                // Convertir de UTC-5 a hora Argentina (UTC-3) = +2 horas
                                hh = (hh + 2) % 24;
                                return `${String(hh).padStart(2, '0')}:${mm}`;
                              }
                              return '-';
                            })()}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-300">Total Entradas</p>
                          <p className="font-bold text-xl text-blue-600 dark:text-blue-400">
                            {(() => {
                              const sumaEntradas = jugadoresDesglose.reduce((sum, j) => sum + (j.entrada_fichas || 0), 0);
                              const buyIn = torneoDesglose.buy_in || 1;
                              return Math.floor(sumaEntradas / buyIn);
                            })()}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-300">Jugadores Únicos</p>
                          <p className="font-bold text-xl text-green-600 dark:text-green-400">
                            {new Set(jugadoresDesglose.map(j => j.jugador_id)).size}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-300">Buy-in</p>
                          <p className="font-semibold text-gray-900 dark:text-white">
                            {torneoDesglose.buy_in ? aplicarRatio(torneoDesglose.buy_in).toFixed(2) : '0.00'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-300">Modalidad</p>
                          <p className="font-semibold text-gray-900 dark:text-white">
                            {torneoDesglose.modalidad || '-'}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* SECCIÓN 2: Árbol Desplegable - Unión → Club → Jugadores */}
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                        📂 Navegación por Estructura
                      </h3>
                      
                      <UnionClubTree 
                        jugadores={jugadoresDesglose}
                        buyInFichas={torneoDesglose.buy_in || 0}
                        aplicarRatio={aplicarRatio}
                        nombreMesa={torneoDesglose.nombre_mesa || ''}
                      />
                    </div>
                  </div>
                )}
              </div>
              
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                <button
                  onClick={() => setShowDesgloseModal(false)}
                  className="px-6 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-700 hover:to-gray-800 font-semibold"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Modal de Análisis Comparativo */}
        {mostrarComparativo && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gradient-to-r from-purple-600 to-blue-600">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  <TrendingUp className="w-6 h-6" />
                  📊 Análisis Comparativo
                </h2>
                <button
                  onClick={() => setMostrarComparativo(false)}
                  className="text-white hover:text-gray-200"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                {/* Selección de Periodos */}
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {/* Periodo 1 */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border-2 border-blue-300 dark:border-blue-700">
                    <h3 className="font-bold text-blue-900 dark:text-blue-300 mb-3">📅 Periodo 1</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Desde
                        </label>
                        <input
                          type="date"
                          value={periodo1Desde}
                          onChange={(e) => setPeriodo1Desde(e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Hasta
                        </label>
                        <input
                          type="date"
                          value={periodo1Hasta}
                          onChange={(e) => setPeriodo1Hasta(e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Periodo 2 */}
                  <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border-2 border-purple-300 dark:border-purple-700">
                    <h3 className="font-bold text-purple-900 dark:text-purple-300 mb-3">📅 Periodo 2</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Desde
                        </label>
                        <input
                          type="date"
                          value={periodo2Desde}
                          onChange={(e) => setPeriodo2Desde(e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Hasta
                        </label>
                        <input
                          type="date"
                          value={periodo2Hasta}
                          onChange={(e) => setPeriodo2Hasta(e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Botón Comparar */}
                <div className="flex justify-center mb-6">
                  <button
                    onClick={realizarAnalisisComparativo}
                    disabled={loadingComparativo}
                    className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 font-bold text-lg disabled:opacity-50"
                  >
                    {loadingComparativo ? 'Comparando...' : '🔍 Comparar Periodos'}
                  </button>
                </div>
                
                {/* Resultados */}
                {datosComparativos && (
                  <div className="space-y-6">
                    {/* Tabla Comparativa */}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-bold">Métrica</th>
                            <th className="px-4 py-3 text-center text-sm font-bold text-blue-700 dark:text-blue-300">
                              Periodo 1<br/>
                              <span className="text-xs font-normal">{datosComparativos.periodo1.fechas}</span>
                            </th>
                            <th className="px-4 py-3 text-center text-sm font-bold text-purple-700 dark:text-purple-300">
                              Periodo 2<br/>
                              <span className="text-xs font-normal">{datosComparativos.periodo2.fechas}</span>
                            </th>
                            <th className="px-4 py-3 text-center text-sm font-bold">Diferencia %</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-4 py-3 font-semibold">🎲 Total Torneos</td>
                            <td className="px-4 py-3 text-center">{datosComparativos.periodo1.metricas.total_torneos}</td>
                            <td className="px-4 py-3 text-center">{datosComparativos.periodo2.metricas.total_torneos}</td>
                            <td className={`px-4 py-3 text-center font-bold ${
                              datosComparativos.diferencias.torneos > 0 ? 'text-green-600' : 
                              datosComparativos.diferencias.torneos < 0 ? 'text-red-600' : 'text-gray-600'
                            }`}>
                              {datosComparativos.diferencias.torneos > 0 ? '↑' : datosComparativos.diferencias.torneos < 0 ? '↓' : '→'} {Math.abs(datosComparativos.diferencias.torneos).toFixed(1)}%
                            </td>
                          </tr>
                          
                          <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-4 py-3 font-semibold">👥 Total Jugadores</td>
                            <td className="px-4 py-3 text-center">{datosComparativos.periodo1.metricas.total_jugadores}</td>
                            <td className="px-4 py-3 text-center">{datosComparativos.periodo2.metricas.total_jugadores}</td>
                            <td className={`px-4 py-3 text-center font-bold ${
                              datosComparativos.diferencias.jugadores > 0 ? 'text-green-600' : 
                              datosComparativos.diferencias.jugadores < 0 ? 'text-red-600' : 'text-gray-600'
                            }`}>
                              {datosComparativos.diferencias.jugadores > 0 ? '↑' : datosComparativos.diferencias.jugadores < 0 ? '↓' : '→'} {Math.abs(datosComparativos.diferencias.jugadores).toFixed(1)}%
                            </td>
                          </tr>
                          
                          <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-4 py-3 font-semibold">📊 Jugadores Promedio/Torneo</td>
                            <td className="px-4 py-3 text-center">{datosComparativos.periodo1.metricas.jugadores_promedio.toFixed(1)}</td>
                            <td className="px-4 py-3 text-center">{datosComparativos.periodo2.metricas.jugadores_promedio.toFixed(1)}</td>
                            <td className={`px-4 py-3 text-center font-bold ${
                              datosComparativos.diferencias.jugadores_promedio > 0 ? 'text-green-600' : 
                              datosComparativos.diferencias.jugadores_promedio < 0 ? 'text-red-600' : 'text-gray-600'
                            }`}>
                              {datosComparativos.diferencias.jugadores_promedio > 0 ? '↑' : datosComparativos.diferencias.jugadores_promedio < 0 ? '↓' : '→'} {Math.abs(datosComparativos.diferencias.jugadores_promedio).toFixed(1)}%
                            </td>
                          </tr>
                          
                          <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-4 py-3 font-semibold">💰 Buy-in Promedio</td>
                            <td className="px-4 py-3 text-center">{aplicarRatio(datosComparativos.periodo1.metricas.buy_in_promedio).toFixed(2)}</td>
                            <td className="px-4 py-3 text-center">{aplicarRatio(datosComparativos.periodo2.metricas.buy_in_promedio).toFixed(2)}</td>
                            <td className={`px-4 py-3 text-center font-bold ${
                              datosComparativos.diferencias.buy_in_promedio > 0 ? 'text-green-600' : 
                              datosComparativos.diferencias.buy_in_promedio < 0 ? 'text-red-600' : 'text-gray-600'
                            }`}>
                              {datosComparativos.diferencias.buy_in_promedio > 0 ? '↑' : datosComparativos.diferencias.buy_in_promedio < 0 ? '↓' : '→'} {Math.abs(datosComparativos.diferencias.buy_in_promedio).toFixed(1)}%
                            </td>
                          </tr>
                          
                          <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-4 py-3 font-semibold">📉 Déficit Garantizado</td>
                            <td className="px-4 py-3 text-center">{aplicarRatio(datosComparativos.periodo1.metricas.overlay_total).toFixed(2)}</td>
                            <td className="px-4 py-3 text-center">{aplicarRatio(datosComparativos.periodo2.metricas.overlay_total).toFixed(2)}</td>
                            <td className={`px-4 py-3 text-center font-bold ${
                              datosComparativos.diferencias.overlay_total > 0 ? 'text-red-600' : 
                              datosComparativos.diferencias.overlay_total < 0 ? 'text-green-600' : 'text-gray-600'
                            }`}>
                              {datosComparativos.diferencias.overlay_total > 0 ? '↑' : datosComparativos.diferencias.overlay_total < 0 ? '↓' : '→'} {Math.abs(datosComparativos.diferencias.overlay_total).toFixed(1)}%
                            </td>
                          </tr>
                          
                          <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-4 py-3 font-semibold">📈 Overlay Promedio/Torneo</td>
                            <td className="px-4 py-3 text-center">{aplicarRatio(datosComparativos.periodo1.metricas.overlay_promedio).toFixed(2)}</td>
                            <td className="px-4 py-3 text-center">{aplicarRatio(datosComparativos.periodo2.metricas.overlay_promedio).toFixed(2)}</td>
                            <td className={`px-4 py-3 text-center font-bold ${
                              datosComparativos.diferencias.overlay_promedio > 0 ? 'text-red-600' : 
                              datosComparativos.diferencias.overlay_promedio < 0 ? 'text-green-600' : 'text-gray-600'
                            }`}>
                              {datosComparativos.diferencias.overlay_promedio > 0 ? '↑' : datosComparativos.diferencias.overlay_promedio < 0 ? '↓' : '→'} {Math.abs(datosComparativos.diferencias.overlay_promedio).toFixed(1)}%
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Leyenda */}
                    <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
                      <p className="text-sm font-semibold mb-2">💡 Interpretación:</p>
                      <div className="grid md:grid-cols-2 gap-2 text-sm">
                        <div><span className="text-green-600 font-bold">↑ Verde</span> = Mejora (más torneos, más jugadores, menos overlay negativo)</div>
                        <div><span className="text-red-600 font-bold">↓ Rojo</span> = Empeora (menos torneos, menos jugadores, más overlay negativo)</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                <button
                  onClick={() => setMostrarComparativo(false)}
                  className="px-6 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-700 hover:to-gray-800 font-semibold"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
